import { ReactNode } from 'react';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { DropdownButton, DropdownButtonRenderListProps } from '@/shared/components/DropdownButton';
import { COLORS } from '@/shared/constants/colors';
import { useProfileStore } from '@/shared/store/profile.store';
import { Icon, IconName } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { BaseProps } from '../types';

type EmptyListProps<T> = {
  title: ReactNode;
  message: ReactNode;
  icon: IconName;
  renderAddNewList?: DropdownButtonRenderListProps<T>;
  onAddNewClick?: (category: T) => void;
};

export const EmptyList = <T,>({
  renderAddNewList,
  icon,
  title,
  message,
  onAddNewClick,
  ...props
}: BaseProps<EmptyListProps<T>>) => {
  const { t } = useTranslation('shared/components', { keyPrefix: 'emptyList' });
  const { isBeneficiary } = useProfileStore();

  return (
    <Container {...props}>
      <Icon name={icon} size="s64" style={{ color: COLORS.systemDark100 }} />
      <TextWrapper>
        <Title bold size={22}>
          {title}
        </Title>
        <p>{message}</p>
      </TextWrapper>
      {!isBeneficiary && !clientConfig.isExported && renderAddNewList && onAddNewClick && (
        <DropdownButton<T>
          style={{ width: '100%', maxWidth: 320 }}
          onSelect={onAddNewClick}
          renderList={renderAddNewList}
        >
          {t('addNew')}
        </DropdownButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: ${COLORS.systemDark200};
  min-height: 308px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 4px;
`;
